<script lang="ts" setup>
	import { hasMonitoringAccess } from "@/utils/monitoringAccess";
	import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
	import type { Component } from "vue";
	import { storeToRefs } from "pinia";
	import { BellRing, Building2, CalendarClock, Coins, Columns3, CornerDownLeft, Database, Download, History, KeyRound, PanelLeft, Plug, Search, Terminal, User, Users } from "lucide-vue-next";
	import { UCC_COMPANY_OVERVIEW_CREDIT_TYPE, UCC_RECOMMENDATIONS_CREDIT_TYPE, useEndpointPermissions } from "@/composables/useEndpointPermissions";
	import { useUser } from "@/stores/user";
	import IconUccSearch from "@/components/icon/UccSearch.vue";

	type Group = "Search" | "Go to" | "Account" | "Actions";

	type Command = {
		id: string;
		group: Group;
		label: string;
		keywords?: string;
		icon: Component;
		run: () => void;
	};

	const GROUP_ORDER: Group[] = ["Search", "Go to", "Account", "Actions"];

	// Shared with the sidebar trigger; anything can open the palette by flipping it.
	const open = useState<boolean>("command-palette-open", () => false);

	const router = useRouter();
	const { user } = storeToRefs(useUser());
	// Monitoring links into the company overview, so it needs the same wallet.
	const { loadEndpointPermissions, hasCreditTypeAccess } = useEndpointPermissions();
	const canRecommendations = computed(() => hasCreditTypeAccess(UCC_RECOMMENDATIONS_CREDIT_TYPE));
	const canCompanyOverview = computed(() => hasCreditTypeAccess(UCC_COMPANY_OVERVIEW_CREDIT_TYPE));
	const collapsedPreference = useCookie<boolean>("leadx_nav_collapsed", {
		default: () => false,
		maxAge: 60 * 60 * 24 * 365,
		sameSite: "lax",
	});

	const query = ref("");
	const activeIndex = ref(0);
	const inputEl = ref<HTMLInputElement | null>(null);
	const listEl = ref<HTMLElement | null>(null);
	let previouslyFocused: HTMLElement | null = null;

	function close() {
		open.value = false;
	}

	function go(path: string, routeQuery?: Record<string, string>) {
		close();
		void router.push({ path, query: routeQuery });
	}

	const staticCommands = computed<Command[]>(() => {
		const u = user.value;
		const isAdmin = u?.admin === true;
		const canCompanies = u?.access_companies === true;
		const canUcc = u?.access_ucc === true;
		const list: Command[] = [];

		if (canCompanies) list.push({ id: "companies", group: "Go to", label: "Companies", keywords: "search leads", icon: Building2, run: () => go("/companies") });
		if (canUcc) {
			if (canRecommendations.value) list.push({ id: "recommendations", group: "Go to", label: "UCC recommendations", keywords: "lookalike customers replicate upload", icon: Building2, run: () => go("/ucc/recommendations") });
			list.push({ id: "ucc", group: "Go to", label: "UCC search", keywords: "filings debtors secured party", icon: IconUccSearch, run: () => go("/ucc") });
			if (canCompanyOverview.value && hasMonitoringAccess(u)) {
				list.push({ id: "monitoring", group: "Go to", label: "UCC monitoring", keywords: "followed companies alerts", icon: BellRing, run: () => go("/ucc/monitoring") });
			}
			list.push({ id: "ucc-reports", group: "Go to", label: "UCC scheduled reports", keywords: "recurring", icon: CalendarClock, run: () => go("/ucc/reports") });
		}
		list.push({ id: "enrichment", group: "Go to", label: "Enrichment", keywords: "email mobile phone find", icon: Database, run: () => go("/enrichment") });
		if (canCompanies || canUcc) list.push({ id: "exports", group: "Go to", label: "Exports", keywords: "downloads csv", icon: Download, run: () => go("/exports") });

		list.push({ id: "profile", group: "Account", label: "Profile & billing", keywords: "settings account", icon: User, run: () => go("/account/profile") });
		list.push({ id: "password", group: "Account", label: "Change password", keywords: "security", icon: KeyRound, run: () => go("/account/profile", { section: "password" }) });
		if (isAdmin) list.push({ id: "credits", group: "Account", label: "Get credits", keywords: "buy purchase top up", icon: Coins, run: () => go("/account/credits") });
		list.push({ id: "credit-history", group: "Account", label: "Credit history", keywords: "usage", icon: History, run: () => go("/account/credit-history") });
		list.push({ id: "crm", group: "Account", label: "CRM integration", keywords: "hubspot salesforce pipedrive zoho dynamics", icon: Plug, run: () => go("/account/crm") });
		if (canUcc) list.push({ id: "ucc-fields", group: "Account", label: "UCC export fields", keywords: "mappings columns", icon: Columns3, run: () => go("/account/ucc-field-mappings") });
		list.push({ id: "api", group: "Account", label: "API", keywords: "keys developer", icon: Terminal, run: () => go("/account/api") });
		if (isAdmin) list.push({ id: "users", group: "Account", label: "Users", keywords: "team seats invite", icon: Users, run: () => go("/account/users") });

		list.push({
			id: "toggle-nav",
			group: "Actions",
			label: collapsedPreference.value ? "Expand sidebar" : "Collapse sidebar",
			keywords: "navigation menu",
			icon: PanelLeft,
			run: () => {
				collapsedPreference.value = !collapsedPreference.value;
				close();
			},
		});

		return list;
	});

	// Free-text searches are offered for whatever is typed, ahead of navigation.
	const searchCommands = computed<Command[]>(() => {
		const term = query.value.trim();
		if (!term) return [];
		const u = user.value;
		const list: Command[] = [];
		if (u?.access_companies === true) {
			list.push({ id: "search-companies", group: "Search", label: `Search companies for “${term}”`, icon: Building2, run: () => go("/companies", { value: term }) });
		}
		if (u?.access_ucc === true) {
			list.push({ id: "search-ucc", group: "Search", label: `Search UCC debtors for “${term}”`, icon: IconUccSearch, run: () => go("/ucc", { field: "name", value: term }) });
		}
		return list;
	});

	// Lower is better; null means no match. Word-start hits beat substring hits,
	// which beat loose in-order character matches.
	function score(cmd: Command, needle: string): number | null {
		const haystack = `${cmd.label} ${cmd.keywords ?? ""}`.toLowerCase();
		if (haystack.startsWith(needle)) return 0;
		if (haystack.includes(` ${needle}`)) return 1;
		if (haystack.includes(needle)) return 2;
		let i = 0;
		for (const ch of haystack) {
			if (ch === needle[i]) i++;
			if (i === needle.length) return 3;
		}
		return null;
	}

	const ranked = computed(() => {
		const needle = query.value.trim().toLowerCase();
		if (!needle) return { items: staticCommands.value, strong: false };
		const scored = staticCommands.value
			.map((cmd) => ({ cmd, s: score(cmd, needle) }))
			.filter((entry): entry is { cmd: Command; s: number } => entry.s !== null)
			.sort((a, b) => a.s - b.s);
		// A word-start hit on a page name means the user is jumping somewhere, so
		// navigation leads; otherwise the typed text is probably a search term.
		return { items: scored.map((entry) => entry.cmd), strong: scored.some((entry) => entry.s <= 1) };
	});

	const results = computed<Command[]>(() => [...searchCommands.value, ...ranked.value.items]);

	const groups = computed(() => {
		const order: Group[] = ranked.value.strong ? ["Go to", "Account", "Actions", "Search"] : GROUP_ORDER;
		return order.map((name) => ({ name, items: results.value.filter((cmd) => cmd.group === name) })).filter((group) => group.items.length > 0);
	});

	// Flattened in display order so keyboard navigation follows what is on screen.
	const flat = computed<Command[]>(() => groups.value.flatMap((group) => group.items));
	const activeCommand = computed(() => flat.value[activeIndex.value] ?? null);
	const activeId = computed(() => (activeCommand.value ? `palette-${activeCommand.value.id}` : undefined));

	function indexOf(cmd: Command) {
		return flat.value.findIndex((entry) => entry.id === cmd.id);
	}

	function move(delta: number) {
		const count = flat.value.length;
		if (!count) return;
		activeIndex.value = (activeIndex.value + delta + count) % count;
	}

	function runActive() {
		activeCommand.value?.run();
	}

	function onKeydown(event: KeyboardEvent) {
		switch (event.key) {
			case "Escape":
				event.preventDefault();
				event.stopPropagation();
				close();
				break;
			case "ArrowDown":
				event.preventDefault();
				move(1);
				break;
			case "ArrowUp":
				event.preventDefault();
				move(-1);
				break;
			case "Home":
				event.preventDefault();
				activeIndex.value = 0;
				break;
			case "End":
				event.preventDefault();
				activeIndex.value = Math.max(0, flat.value.length - 1);
				break;
			case "Enter":
				event.preventDefault();
				runActive();
				break;
		}
	}

	function onGlobalKeydown(event: KeyboardEvent) {
		const isShortcut = (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k";
		if (!isShortcut) return;
		event.preventDefault();
		open.value = !open.value;
	}

	watch(query, () => {
		activeIndex.value = 0;
	});

	watch(activeIndex, async () => {
		await nextTick();
		listEl.value?.querySelector<HTMLElement>(".palette-item.active")?.scrollIntoView({ block: "nearest" });
	});

	watch(open, async (isOpen) => {
		if (!process.client) return;
		if (isOpen) {
			previouslyFocused = document.activeElement as HTMLElement | null;
			query.value = "";
			activeIndex.value = 0;
			document.documentElement.classList.add("overflow-y-hidden");
			await nextTick();
			inputEl.value?.focus();
		} else {
			document.documentElement.classList.remove("overflow-y-hidden");
			previouslyFocused?.focus?.();
			previouslyFocused = null;
		}
	});

	onMounted(() => {
		void loadEndpointPermissions();
		window.addEventListener("keydown", onGlobalKeydown);
	});

	onBeforeUnmount(() => {
		window.removeEventListener("keydown", onGlobalKeydown);
		document.documentElement.classList.remove("overflow-y-hidden");
	});
</script>

<template>
	<Teleport to="body">
		<Transition name="palette">
			<div v-if="open" class="palette-root" @keydown="onKeydown">
				<div class="palette-backdrop" @click="close"></div>

				<div class="palette" role="dialog" aria-modal="true" aria-label="Search and commands">
					<div class="palette-input">
						<Search :size="18" class="palette-input-icon" aria-hidden="true" />
						<input
							ref="inputEl"
							v-model="query"
							type="text"
							role="combobox"
							aria-expanded="true"
							aria-controls="palette-list"
							aria-autocomplete="list"
							:aria-activedescendant="activeId"
							placeholder="Search companies or UCC filings, or jump to a page…"
							autocomplete="off"
							autocorrect="off"
							spellcheck="false"
						/>
						<kbd class="palette-kbd">esc</kbd>
					</div>

					<div id="palette-list" ref="listEl" class="palette-list" role="listbox" aria-label="Commands">
						<template v-for="group in groups" :key="group.name">
							<div class="palette-group-label" role="presentation">{{ group.name }}</div>
							<button
								v-for="cmd in group.items"
								:key="cmd.id"
								:id="`palette-${cmd.id}`"
								type="button"
								role="option"
								class="palette-item"
								:class="{ active: activeCommand?.id === cmd.id }"
								:aria-selected="activeCommand?.id === cmd.id"
								tabindex="-1"
								@mousemove="activeIndex = indexOf(cmd)"
								@click="cmd.run()"
							>
								<component :is="cmd.icon" :size="16" class="palette-item-icon" aria-hidden="true" />
								<span class="palette-item-label">{{ cmd.label }}</span>
								<CornerDownLeft v-if="activeCommand?.id === cmd.id" :size="14" class="palette-item-enter" aria-hidden="true" />
							</button>
						</template>

						<div v-if="!flat.length" class="palette-empty">No matches for “{{ query }}”</div>
					</div>

					<div class="palette-foot">
						<span><kbd class="palette-kbd">↑</kbd><kbd class="palette-kbd">↓</kbd> navigate</span>
						<span><kbd class="palette-kbd">↵</kbd> select</span>
						<span><kbd class="palette-kbd">esc</kbd> close</span>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<style scoped lang="scss">
	.palette-root {
		position: fixed;
		inset: 0;
		z-index: 1200;
		display: flex;
		justify-content: center;
		align-items: flex-start;
		padding-top: min(18vh, 160px);
	}

	.palette-backdrop {
		position: absolute;
		inset: 0;
		background: rgba(17, 18, 20, 0.32);
		backdrop-filter: blur(2px);
	}

	.palette {
		position: relative;
		display: flex;
		flex-direction: column;
		width: min(600px, calc(100vw - 32px));
		max-height: min(60vh, 520px);
		background: var(--bg-surface);
		border: 1px solid var(--border);
		border-radius: var(--radius-xl);
		box-shadow: var(--shadow-lg);
		overflow: hidden;
	}

	.palette-input {
		display: flex;
		align-items: center;
		gap: 12px;
		height: 52px;
		padding: 0 16px;
		border-bottom: 1px solid var(--border);
		flex-shrink: 0;

		.palette-input-icon {
			color: var(--text-muted);
			flex-shrink: 0;
		}

		input {
			@include GoogleSans(false);
			flex: 1;
			min-width: 0;
			height: 100%;
			border: 0;
			outline: 0;
			background: transparent;
			color: var(--text);
			font-size: 15px;
			font-weight: 500;
			letter-spacing: -0.1px;

			&::placeholder {
				color: var(--text-muted);
			}
		}
	}

	.palette-kbd {
		@include GoogleSans(false);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 20px;
		height: 20px;
		padding: 0 6px;
		border: 1px solid var(--border);
		border-radius: var(--radius-sm);
		background: var(--bg-subtle);
		color: var(--text-muted);
		font-size: 11px;
		font-weight: 500;
		line-height: 1;
	}

	.palette-list {
		overflow-y: auto;
		padding: 8px;
		min-height: 0;
	}

	.palette-group-label {
		@include GoogleSans(false);
		padding: 10px 10px 6px;
		color: var(--text-faint);
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
	}

	.palette-item {
		@include GoogleSans(false);
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		height: 40px;
		padding: 0 10px;
		border: 0;
		border-radius: var(--radius-md);
		background: transparent;
		color: var(--text);
		font-size: 14px;
		font-weight: 500;
		letter-spacing: -0.1px;
		text-align: left;
		cursor: pointer;

		.palette-item-icon {
			flex-shrink: 0;
			color: var(--text-muted);
		}

		.palette-item-label {
			flex: 1;
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.palette-item-enter {
			flex-shrink: 0;
			color: var(--text-faint);
		}

		&.active {
			background: var(--bg-subtle);

			.palette-item-icon {
				color: var(--text);
			}
		}
	}

	.palette-empty {
		@include GoogleSans(false);
		padding: 28px 12px;
		color: var(--text-muted);
		font-size: 14px;
		text-align: center;
	}

	.palette-foot {
		@include GoogleSans(false);
		display: flex;
		align-items: center;
		gap: 16px;
		height: 36px;
		padding: 0 16px;
		border-top: 1px solid var(--border);
		background: var(--bg-subtle);
		color: var(--text-muted);
		font-size: 12px;
		flex-shrink: 0;

		span {
			display: inline-flex;
			align-items: center;
			gap: 6px;
		}
	}

	// Motion: fade the backdrop, and let the panel settle down into place.
	.palette-enter-active,
	.palette-leave-active {
		transition: opacity var(--dur-fast) var(--ease-out);

		.palette {
			transition:
				transform var(--dur) var(--ease-out),
				opacity var(--dur) var(--ease-out);
		}
	}

	.palette-enter-from,
	.palette-leave-to {
		opacity: 0;

		.palette {
			opacity: 0;
			transform: translateY(-6px) scale(0.985);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.palette-enter-active,
		.palette-leave-active,
		.palette-enter-active .palette,
		.palette-leave-active .palette {
			transition: none;
		}
	}
</style>
